
import * as React from "react"
import { Check,ChevronsUpDown,X,Plus } from "lucide-react"

import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
Command,
CommandEmpty,
CommandGroup,
CommandInput,
CommandItem,
CommandList,
} from "@/components/ui/command"
import {
Popover,
PopoverContent,
PopoverTrigger,
} from "@/components/ui/popover"

type Option = {
  label: string
  value: string
}

interface MultiSelectProps {
  options: Option[]
  selected: string[]
  onChange: (values: string[]) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  onAddNew?: (value: string, color?: string) => void
  createWithColor?: boolean
  /** Values that are always selected and cannot be removed (no X, not deselectable). */
  lockedValues?: string[]
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = "Select items...",
  className,
  disabled = false,
  onAddNew,
  createWithColor = false,
  lockedValues = [],
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState("")
  const [createColor, setCreateColor] = React.useState("#3b82f6")

  const handleUnselect = (value: string) => {
    if (lockedValues.includes(value)) return
    onChange(selected.filter((s) => s !== value))
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between h-auto min-h-10 px-3 py-2 hover:bg-background",
            className
          )}
          onClick={() => {
            if (!disabled) setOpen(!open)
          }}
          disabled={disabled}
        >
          <div className="flex flex-wrap gap-1 items-center justify-start max-w-full">
            {selected.length === 0 && <span className="text-muted-foreground font-normal">{placeholder}</span>}

            {selected.length > 0 && selected.map((item) => {
              const option = options.find((o) => o.value === item)
              if (!option) return null
              return (
                <Badge variant="secondary" key={item} className="mr-1 mb-1">
                  {option.label}
                  {!disabled && !lockedValues.includes(item) && (
                    <div
                      className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleUnselect(item)
                        }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleUnselect(item)
                      }}
                    >
                      <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                    </div>
                  )}
                </Badge>
              )
            })}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command className="w-full">
          <CommandInput placeholder="Search..." value={inputValue} onValueChange={setInputValue} />
          <CommandList className="max-h-64 overflow-auto">
            <CommandEmpty>
              {onAddNew && inputValue.trim().length > 0 ? (
                <div
                  className="py-2 px-3 text-sm cursor-pointer hover:bg-accent rounded-sm flex items-center justify-start gap-2 text-muted-foreground hover:text-foreground group"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onAddNew(inputValue, createWithColor ? createColor : undefined);
                    setInputValue("");
                  }}
                >
                  <Plus className="w-3 h-3" />
                  <span className="flex-1">Create "{inputValue}"</span>
                  {createWithColor && (
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <span className="text-xs text-muted-foreground group-hover:text-foreground/80">Color:</span>
                      <input
                        type="color"
                        value={createColor}
                        onChange={(e) => setCreateColor(e.target.value)}
                        className="w-5 h-5 p-0 border-0 rounded-full overflow-hidden shrink-0 cursor-pointer bg-transparent"
                        title="Select Color"
                      />
                    </div>
                  )}
                </div>
              ) : "No item found."}
            </CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() => {
                    if (selected.includes(option.value)) {
                      if (lockedValues.includes(option.value)) return
                      onChange(selected.filter((s) => s !== option.value))
                    } else {
                      onChange([...selected, option.value])
                    }
                    setInputValue("")
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selected.includes(option.value)
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
